import React, { createContext, useEffect, useImperativeHandle, useRef, useState } from 'react'
import BaseModal from '@/component/modal/BaseModal.tsx'
import useQueryGetAlertBioLive from '@/api/notification/getAlertBioLive.ts'
import useQueryGetAlertBioDetail from '@/api/notification/getAlertBioDetail.ts'
import { IAlertBio } from '@/api/notification/getNotificationBio.ts'
import BioAlertDetail from '@/pages/notification/bio/BioAlertDetail.tsx'
import BioAlertItem from '@/pages/notification/bio/BioAlertItem.tsx'
import { isEmptyArray, isNotEmptyArray } from '@/util/array.ts'
import { useTranslation } from 'react-i18next'
import { decideLiveAlertSync, ILiveAlertSignature, toLiveAlertSignature } from '@/util/emergencyAlertLiveSync.ts'
import PulseIcon from '@/assets/ic_pulse.svg?react'

export interface IProps {
    className?: string
    refetchAlerts?: () => void
    defaultId?: number
}

export type IAlertBioContextProps = {
    id?: number
    setId: (id: number) => void
    refetchList: () => void
    refetchAndResetSelection: () => void
}

export const BioAlertModalContext = createContext<IAlertBioContextProps>({
    id: undefined,
    setId: () => {
    },
    refetchList: () => {
    },
    refetchAndResetSelection: () => {
    }
})


const BioAlertModal = ({ defaultId, refetchAlerts }: IProps, ref: any) => {
    const { t } = useTranslation()

    const modalRef = useRef<any>()
    const [id, setId] = useState<number>()
    const { data: bioAlerts, refetch: refetchList } = useQueryGetAlertBioLive(true)
    const { data: bioDetail, refetch: refetchBioDetail } = useQueryGetAlertBioDetail(id)

    useImperativeHandle(ref, () => ({
        show: () => {
            modalRef.current.show()
        },
        close: () => {
            modalRef.current.close()
        }
    }))

    const resolveSelection = (newAlerts?: IAlertBio[]) => {
        setId(undefined)

        if (isEmptyArray(newAlerts)) {
            modalRef.current.close()
            return
        }

        const myAlerts = newAlerts?.filter(item => item?.alert?.isMyTask == true)
        if (myAlerts && isNotEmptyArray(myAlerts)) {
            setId(myAlerts[0].id)
            return
        }

        const notMyAlerts = newAlerts?.filter(item => !item?.alert?.taskManagerAt)
        if (notMyAlerts && isNotEmptyArray(notMyAlerts)) {
            setId(notMyAlerts[0].id)
            return
        }
    }

    const refetchAndResetSelection = async () => {
        const { data: resAlerts } = await refetchList()
        resolveSelection(resAlerts?.bios)
    }

    const selectedLiveSignature = toLiveAlertSignature(bioAlerts?.bios?.find(item => item.id === id))
    const lastLiveSignatureRef = useRef<{ id: number; signature?: ILiveAlertSignature }>()

    useEffect(() => {
        if (id === undefined) {
            lastLiveSignatureRef.current = undefined
            return
        }

        const previous = lastLiveSignatureRef.current?.id === id
            ? lastLiveSignatureRef.current.signature
            : undefined
        lastLiveSignatureRef.current = { id, signature: selectedLiveSignature }

        const action = decideLiveAlertSync(previous, selectedLiveSignature, bioDetail)
        if (action === 'refetch-detail') {
            refetchBioDetail()
        } else if (action === 'advance-selection') {
            resolveSelection(bioAlerts?.bios)
        }
    }, [
        id,
        selectedLiveSignature !== undefined,
        selectedLiveSignature?.assignedAt,
        selectedLiveSignature?.isMyTask
    ])

    return (
        <BioAlertModalContext.Provider value={{ id, setId, refetchList, refetchAndResetSelection }}>
            <BaseModal
                ref={modalRef}
                width={'1200'}
                panelClassName="bio-alert-console"
                onOpenCallback={() => {
                    if (id) return
                    if (defaultId) {
                        setId(defaultId)
                    } else if ((bioAlerts?.bios?.length || 0) > 0) {
                        setId(bioAlerts?.bios[0]!.id)
                    }
                }}
                onCloseCallback={() => {
                    setId(undefined)
                    refetchAlerts && refetchAlerts()
                }}>
                <header className="bio-alert-console__header">
                    <div className="bio-alert-console__header-icon" aria-hidden><PulseIcon /></div>
                    <div>
                        <p>CAREARLY BIO MONITORING</p>
                        <h2>{t('notification.bioModal.title')}</h2>
                    </div>
                    <div className="bio-alert-console__live">
                        <i />
                        {t('notification.v2.bioModal.liveDetection')}
                        <strong>{bioAlerts?.bios?.length || 0}</strong>
                    </div>
                </header>
                <div className="bio-alert-console__body">
                    <div className="bio-alert-console__layout">
                        <aside className="bio-alert-console__rail">
                            <div className="bio-alert-console__queue-heading">
                                <div>
                                    <span>LIVE QUEUE</span>
                                    <strong>{t('notification.v2.bioModal.pendingQueue')}</strong>
                                </div>
                                <b>{bioAlerts?.bios?.length || 0}</b>
                            </div>
                            <div className="app-scrollbar bio-alert-console__queue">
                                {Number(bioAlerts?.bios?.length) > 0
                                    ? bioAlerts?.bios?.map(item => <BioAlertItem key={item?.id} {...item} />)
                                    : <div className="bio-alert-console__empty">
                                        <span><PulseIcon aria-hidden /></span>
                                        <strong>{t('notification.bioModal.empty')}</strong>
                                        <p>{t('notification.v2.bioModal.emptyHint')}</p>
                                    </div>}
                            </div>
                        </aside>
                        <BioAlertDetail />
                    </div>
                </div>
            </BaseModal>
        </BioAlertModalContext.Provider>

    )
}

export default React.forwardRef(BioAlertModal)
